import { default as React, useRef, useState } from 'react';
import { DropTargetMonitor, useDrag, useDrop, XYCoord } from 'react-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import {
  InternalApiAlertType,
  InternalApiEntityType,
  InternalApiScenarioType,
  InternalApiScreeningAlertStatus,
} from '../../api-internal';
import { SalvButton, SalvLabel, Size } from '@salvapp/ui-components';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import { useNavigate } from 'react-router';
import { ApiSanctionType } from '../../api';
import { Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './AlertStatusRow.scss';
import { DropdownWrapper } from '../../common/DropdownWrapper';
import useCustomLists from '../../custom-lists/useCustomLists';
import QuickStatusNotesModal from '../alert/status/QuickStatusNotesModal';

export interface DragItem {
  index: number;
  id: string;
  type: string;
}

interface StatusRowProps {
  status: InternalApiScreeningAlertStatus;
  type: InternalApiAlertType;
  isHistoric?: boolean;
  isDraggable: boolean;
  index: number;
  moveStatus: (dragIndex: number, hoverIndex: number) => void;
  updateSortOrder: () => void;
}

const screeningDisplayNames: { [key: string]: string } = {
  [ApiSanctionType.SANCTION]: 'Sanctions',
  [ApiSanctionType.PEP]: 'PEP',
  [ApiSanctionType.ADVERSE_MEDIA]: 'Adverse media',
  [ApiSanctionType.ADVERSE_MEDIA_ENTITY]: 'Adverse media entity',
};

const bridgeDisplayNames: { [key: string]: string } = {
  BRIDGE: 'Bridge',
};

const manualDisplayNames: { [key: string]: string } = {
  MANUAL: 'Manual',
};

const AlertStatusRow = ({
  isDraggable,
  status,
  type,
  index,
  moveStatus,
  updateSortOrder,
  isHistoric = false,
}: StatusRowProps) => {
  const customLists = useCustomLists();

  const [showEditModal, setShowEditModal] = useState<boolean>();

  const navigate = useNavigate();

  const ref = useRef<HTMLTableRowElement>(null);

  const monitoringDisplayNames: { [key: string]: string } = {
    [InternalApiScenarioType.ONLINE]: 'Real-time',
    [InternalApiScenarioType.OFFLINE]: 'Post-event',
    [InternalApiScenarioType.PERIODIC]: 'Periodic',
  };

  const [, drop] = useDrop({
    accept: 'STATUS_ROW',
    hover(item: DragItem, monitor: DropTargetMonitor<DragItem>) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveStatus(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop() {
      updateSortOrder();
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'STATUS_ROW',
    item: { type: 'STATUS_ROW', id: status.id, index: index },
    canDrag: isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const getAlertTypeDisplayNames = () => {
    switch (type) {
      case InternalApiAlertType.SCREENING:
        return screeningDisplayNames;
      case InternalApiAlertType.MONITORING:
        return monitoringDisplayNames;
      case InternalApiAlertType.BRIDGE:
        return bridgeDisplayNames;
      case InternalApiAlertType.MANUAL:
        return manualDisplayNames;
    }
  };
  const alertTypeDisplayNames = getAlertTypeDisplayNames();

  const getAlertTypeRestrictions = () => {
    const configuration = status._configuration!;
    switch (type) {
      case InternalApiAlertType.SCREENING:
        return configuration.screeningLists!.restrictedTo!.filter(
          (enabledList) => enabledList !== ApiSanctionType.CUSTOM_SCREENING_LIST
        );
      case InternalApiAlertType.MONITORING:
        return configuration.monitoring!.restrictedTo!;
      case InternalApiAlertType.BRIDGE:
        return ['BRIDGE'];
      case InternalApiAlertType.MANUAL:
        return ['MANUAL'];
    }
  };
  const alertTypeRestrictions = getAlertTypeRestrictions().map((alertType) => alertTypeDisplayNames[alertType]);

  if (type === InternalApiAlertType.SCREENING && customLists.length) {
    const enabledCustomListNames = status._configuration!.customLists?.restrictedTo?.map(
      (enabledCustomListId) =>
        customLists.find((customList) => customList.id === enabledCustomListId)?.name ??
        `Custom list ${enabledCustomListId}`
    );
    alertTypeRestrictions.push(...(enabledCustomListNames || []));
  }

  const getEntityTypeDisplayNames = () => {
    return status._configuration!.entityTypes!.map((type) => {
      switch (type) {
        case InternalApiEntityType.TRANSACTION:
          return 'Transaction';
        case InternalApiEntityType.PERSON:
          return 'Person';
      }
    });
  };
  const entityTypeDisplayNames = getEntityTypeDisplayNames();

  const showSelection = () => {
    if (status.isSystem) {
      return (
        <DropdownWrapper className='alert-status-row-dropdown'>
          <OverlayTrigger
            placement='top'
            overlay={(props) => (
              <Tooltip id='disabled-tooltip' {...props}>
                This status is reserved by Salv
              </Tooltip>
            )}
          >
            <Dropdown.Toggle id='actions-dropdown' as={Button} size='sm' className='btn-white toggle' disabled={true}>
              •••
            </Dropdown.Toggle>
          </OverlayTrigger>
        </DropdownWrapper>
      );
    } else {
      return (
        <DropdownWrapper className='alert-status-row-dropdown'>
          <Dropdown.Toggle id='actions-dropdown' as={Button} size='sm' className='btn-white toggle'>
            •••
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <SalvButton
              label='Edit'
              onClick={() => navigate(`/screening/statuses/setup/${status.id}`)}
              variant='muted'
              size={Size.SMALL}
              className='dropdown-item no-border'
            />
            <SalvButton
              label='Quick Status Notes'
              onClick={() => setShowEditModal(true)}
              variant='muted'
              size={Size.SMALL}
              className='dropdown-item no-border'
            />
          </Dropdown.Menu>
        </DropdownWrapper>
      );
    }
  };

  return (
    <>
      <tr ref={ref} style={{ opacity: opacity ?? 1 }} data-testid='status-row'>
        {isDraggable && (
          <td data-testid='grip-icon'>
            <FontAwesomeIcon icon={faGripVertical} className='text-muted cursor-grab' />
          </td>
        )}
        <td>{status.textName}</td>
        <td className='code'>{status.code}</td>
        <td>
          <div className='labels'>
            {alertTypeRestrictions.map((it) => (
              <SalvLabel key={it} removable={false} variant='secondary'>
                {it}
              </SalvLabel>
            ))}
          </div>
        </td>
        <td>
          <div className='labels'>
            {entityTypeDisplayNames?.map((it) => (
              <SalvLabel key={it} removable={false} variant='secondary'>
                {it}
              </SalvLabel>
            ))}
          </div>
        </td>
        <td>
          <FormattedDateTime date={status.startTime} showTime={true} />
        </td>
        {isHistoric && (
          <td>
            <FormattedDateTime date={status.endTime} showTime={true} />
          </td>
        )}
        <td className='text-center'>{status.isFinal && <FontAwesomeIcon icon={faCheck} />}</td>
        <td className='text-center'>{status.isGoodlistable && <FontAwesomeIcon icon={faCheck} />}</td>
        <td className='text-center'>{showSelection()}</td>
      </tr>
      {showEditModal && (
        <QuickStatusNotesModal showModal={true} onClose={() => setShowEditModal(false)} status={status} />
      )}
    </>
  );
};
export default AlertStatusRow;
